
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TranslationText } from "@/components/i18n/TranslationText";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Show a loading spinner while authentication state is being determined
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            <TranslationText i18nKey="auth.loading">
              Loading...
            </TranslationText>
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            <TranslationText i18nKey="auth.error.title">
              Authentication Error
            </TranslationText>
          </AlertTitle>
          <AlertDescription className="mt-2">
            <p className="mb-4">
              <TranslationText i18nKey="auth.error.description">
                There was an error checking your authentication status. This may be a temporary issue.
              </TranslationText>
            </p>
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="mr-2"
            >
              <TranslationText i18nKey="auth.error.reload">
                Reload page
              </TranslationText>
            </Button>
            <Button 
              onClick={() => window.location.href = '/login'}
            >
              <TranslationText i18nKey="auth.error.login">
                Go to login
              </TranslationText>
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to landing page and preserve the intended destination
    console.log("User is not authenticated, redirecting from:", location.pathname);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
